import { useState, useEffect, useCallback } from 'react';

const IMAGE_SCALE_KEY = 'askaldo-image-scale-preference';
const DEFAULT_SCALE = 25; // -1 = hidden, 0 = small (280px), 25 = default, 50 = medium (740px), 100 = full size

export const useImageScale = () => {
  const [imageScale, setImageScaleState] = useState<number>(() => {
    // Initialize from localStorage
    try {
      const stored = localStorage.getItem(IMAGE_SCALE_KEY);
      if (stored !== null) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= -1 && parsed <= 100) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn('Failed to load image scale preference:', error);
    }
    return DEFAULT_SCALE;
  });

  const setImageScale = useCallback((scale: number) => {
    // Allow -1 for hidden, clamp between -1 and 100
    const clampedScale = Math.max(-1, Math.min(100, scale));
    setImageScaleState(clampedScale);
    
    // Persist to localStorage
    try {
      localStorage.setItem(IMAGE_SCALE_KEY, clampedScale.toString());
    } catch (error) {
      console.warn('Failed to save image scale preference:', error);
    }
  }, []);

  // Compute the actual max width based on scale
  // -1 = hidden, 0% = 280px, 100% = 100% of content area (no artificial limit)
  const getMaxWidth = useCallback(() => {
    if (imageScale === -1) {
      return 'hidden'; // Special value to indicate hidden
    }
    if (imageScale === 100) {
      return '100%'; // Full size - let image use natural size up to container width
    }
    // Linear interpolation: 280px to 1200px
    const minSize = 280;
    const maxSize = 1200;
    const size = minSize + (imageScale / 100) * (maxSize - minSize);
    return `${Math.round(size)}px`;
  }, [imageScale]);

  return {
    imageScale,
    setImageScale,
    getMaxWidth,
    isFullSize: imageScale === 100,
    isHidden: imageScale === -1
  };
};

